/**
 * Caption Showcase Component
 * 
 * Interactive demonstration of the advanced caption system.
 * Shows live previews of different animations, fonts, and effects
 * with large text sizes for easy visibility.
 * 
 * Usage:
 * import { CaptionShowcase } from './text/CaptionShowcase';
 * <CaptionShowcase />
 */

import React, { useState } from 'react';
import { useCurrentFrame } from 'remotion';
import { AdvancedTextEngine } from './AdvancedTextEngine';
import { 
    AdvancedTextConfig, 
    TextPageData, 
    TextToken,
    DEFAULT_ADVANCED_TEXT_CONFIG 
} from './types';
import { FontLoader } from './FontUtils';

// Unique fonts for the demo (easily visible and distinctive)
const DEMO_FONTS = [
    { name: 'Bangers', family: 'Bangers' },
    { name: 'Titan One', family: 'Titan One' },  
    { name: 'Black Han Sans', family: 'Black Han Sans' },
    { name: 'Fredoka One', family: 'Fredoka One' },
    { name: 'Rubik Wet Paint', family: 'Rubik Wet Paint' },
    { name: 'Playfair Display', family: 'Playfair Display' },
    { name: 'Creepster', family: 'Creepster' },
    { name: 'Satisfy', family: 'Satisfy' }
];

// Animation demonstrations with large, visible text
const ANIMATION_DEMOS = [
    {
        name: 'Highlight Animation',
        text: 'HIGHLIGHT EACH WORD AS IT SPEAKS',
        animation: 'highlight' as const,
        color: '#ffffff',
        highlight: '#ff6b35',
        outline: 'stroke' as const,
        background: 'translucent' as const
    },
    {
        name: 'Pop Animation', 
        text: 'Words POP with Spring Physics',
        animation: 'pop' as const,
        color: '#4ecdc4',
        highlight: '#4ecdc4',
        outline: '3d-shadow' as const,
        background: 'none' as const
    },
    {
        name: 'Karaoke Style',
        text: 'Karaoke Style Progressive Word Reveal',
        animation: 'karaoke' as const,
        color: '#ffffff',
        highlight: '#ff4757',
        outline: 'glow' as const,
        background: 'colored' as const
    },
    {
        name: 'Boxed Animation',
        text: 'EACH WORD GETS ANIMATED BOXES',
        animation: 'boxed' as const,
        color: '#000000',
        highlight: '#ffff00',
        outline: 'none' as const,
        background: 'none' as const
    },
    {
        name: 'Scale Animation',
        text: 'Words Scale Up with Smooth Motion',
        animation: 'scale' as const,
        color: '#ffffff',
        highlight: '#ff6b6b',
        outline: 'stroke' as const,
        background: 'none' as const
    },
    {
        name: 'Slide Up Animation',
        text: 'Smooth Upward Sliding Motion',
        animation: 'slide_up' as const,
        color: '#f39c12',
        highlight: '#f39c12',
        outline: '3d-shadow' as const,
        background: 'none' as const
    },
    {
        name: 'Slide Left Animation',
        text: 'DYNAMIC HORIZONTAL SLIDE ENTRY',
        animation: 'slide_left' as const,
        color: '#ffffff',
        highlight: '#ff4081',
        outline: 'glow' as const,
        background: 'translucent' as const
    }
];

export const CaptionShowcase: React.FC = () => {
    const [selectedFont, setSelectedFont] = useState(DEMO_FONTS[0]);
    const [selectedDemo, setSelectedDemo] = useState(ANIMATION_DEMOS[0]);
    const [isPlaying, setIsPlaying] = useState(true);
    
    // Simulate time progression for animation
    const frame = useCurrentFrame();
    const fps = 30;
    
    // Create demo text tokens
    const demoText = selectedDemo.text;
    const words = demoText.split(' ');
    const totalDuration = 8; // 8 seconds total
    // FIXED: Make word timing 3x faster (divide word duration by 3)
    const wordDuration = (totalDuration * 1000) / words.length / 3; // Duration per word in ms
    
    const tokens: TextToken[] = words.map((word, index) => ({
        text: word,
        fromMs: index * wordDuration,
        toMs: (index + 1) * wordDuration,
        index: index
    }));

    // Create page data
    const pageData: TextPageData = {
        text: demoText,
        tokens: tokens,
        startMs: 0
    };

    // Create advanced text configuration
    const config: AdvancedTextConfig = {
        primaryColor: selectedDemo.color,
        currentSpokenWordColor: selectedDemo.highlight,
        fontFamily: selectedFont.family,
        maxFontSize: 84, // Extra large for visibility
        outline: selectedDemo.outline,
        background: selectedDemo.background,
        textAnimation: selectedDemo.animation,
        borderRadius: 0.2,
        captionCase: selectedDemo.animation === 'boxed' ? 'uppercase' : 'standard',
        hidePunctuation: false,
        containerPadding: { x: 0.15, y: 0.1 },
        boxPadding: { x: 0.12, y: 0.08 }
    };

    // Calculate current frame for animation (loop every 8 seconds)
    const animationFrame = isPlaying ? (frame % (8 * fps)) : 0;

    return (
        <div style={{
            padding: '20px',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#f5f5f5',
            minHeight: '100vh'
        }}>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '40px'
                }}>
                    <h1 style={{
                        fontSize: '2.5rem',
                        color: '#2c3e50',
                        marginBottom: '10px'
                    }}>
                        🎬 Advanced Caption System Demo
                    </h1>
                    <p style={{
                        fontSize: '1.2rem',
                        color: '#7f8c8d',
                        marginBottom: '20px'
                    }}>
                        Featuring large text, unique fonts, and professional animations
                    </p>
                </div>

                {/* Controls */}
                <div style={{
                    display: 'flex',
                    gap: '20px',
                    marginBottom: '40px',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                }}>
                    {/* Font Selector */}
                    <div style={{ minWidth: '200px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            🔤 Font Family:
                        </label>
                        <select
                            value={selectedFont.name}
                            onChange={(e) => {
                                const font = DEMO_FONTS.find(f => f.name === e.target.value);
                                if (font) setSelectedFont(font);
                            }}
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '16px',
                                border: '2px solid #3498db',
                                borderRadius: '8px',
                                backgroundColor: 'white'
                            }}
                        >
                            {DEMO_FONTS.map(font => (
                                <option key={font.name} value={font.name}>
                                    {font.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Animation Selector */}
                    <div style={{ minWidth: '250px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontWeight: '600',
                            color: '#2c3e50'
                        }}>
                            ✨ Animation Type:
                        </label>
                        <select
                            value={selectedDemo.name}
                            onChange={(e) => {
                                const demo = ANIMATION_DEMOS.find(d => d.name === e.target.value);
                                if (demo) setSelectedDemo(demo);
                            }}
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '16px',
                                border: '2px solid #e74c3c',
                                borderRadius: '8px',
                                backgroundColor: 'white'
                            }}
                        >
                            {ANIMATION_DEMOS.map(demo => (
                                <option key={demo.name} value={demo.name}>
                                    {demo.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Play/Pause Control */}
                    <div style={{ display: 'flex', alignItems: 'end' }}>
                        <button
                            onClick={() => setIsPlaying(!isPlaying)}
                            style={{
                                padding: '12px 20px',
                                fontSize: '16px',
                                fontWeight: '600',
                                color: 'white',
                                backgroundColor: isPlaying ? '#e74c3c' : '#27ae60',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {isPlaying ? '⏸️ Pause' : '▶️ Play'}
                        </button>
                    </div>
                </div>

                {/* Demo Preview */}
                <div style={{
                    backgroundColor: '#1a1a2e',
                    padding: '60px 20px',
                    borderRadius: '20px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
                    marginBottom: '40px',
                    minHeight: '300px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background gradient */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        opacity: 0.1
                    }} />

                    {/* Caption Demo */}
                    <FontLoader 
                        fonts={[selectedFont.family]} 
                        initializeClipCreatorFonts={true}
                    >
                        <div style={{
                            position: 'relative',
                            zIndex: 1,
                            width: '100%',
                            height: '200px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <AdvancedTextEngine
                                page={pageData}
                                config={config}
                                videoConfig={{ fps: fps, width: 1920 }}
                                currentFrame={animationFrame}
                                position="center"
                            />
                        </div>
                    </FontLoader>
                </div>

                {/* Information Panel */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px',
                    marginBottom: '40px'
                }}>
                    {/* Current Configuration */}
                    <div style={{
                        backgroundColor: 'white',
                        padding: '25px',
                        borderRadius: '15px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>
                            🎨 Current Configuration
                        </h3>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            <li style={{ marginBottom: '8px' }}>
                                <strong>Font:</strong> {selectedFont.name}
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                <strong>Size:</strong> 84px (Extra Large)
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                <strong>Animation:</strong> {selectedDemo.animation}
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                <strong>Outline:</strong> {selectedDemo.outline}
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                <strong>Background:</strong> {selectedDemo.background}
                            </li>
                        </ul>
                    </div>

                    {/* Features */}
                    <div style={{
                        backgroundColor: 'white',
                        padding: '25px',
                        borderRadius: '15px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                    }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>
                            ⚡ Advanced Features
                        </h3>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            <li style={{ marginBottom: '8px' }}>
                                ✅ Word-by-word animation timing
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                ✅ Professional color contrast
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                ✅ Spring physics animations
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                ✅ 36+ Google Fonts available
                            </li>
                            <li style={{ marginBottom: '8px' }}>
                                ✅ SVG-based multi-layer rendering
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Quick Stats */}
                <div style={{
                    textAlign: 'center',
                    padding: '30px',
                    backgroundColor: '#ecf0f1',
                    borderRadius: '15px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '40px',
                        flexWrap: 'wrap'
                    }}>
                        <div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e74c3c' }}>14</div>
                            <div style={{ color: '#7f8c8d' }}>Text Animations</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#3498db' }}>6</div>
                            <div style={{ color: '#7f8c8d' }}>Outline Effects</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>36+</div>
                            <div style={{ color: '#7f8c8d' }}>Google Fonts</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f39c12' }}>60fps</div>
                            <div style={{ color: '#7f8c8d' }}>Smooth Animation</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CaptionShowcase;